import React, { useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Car,
  CheckCircle2,
  Clock3,
  MapPin,
  MessageCircle,
  Phone,
  User,
  Users,
  X,
} from "lucide-react";
import BookingSuccess from "./BookingSuccess";
import { rideService } from "../../services/rideService";

const Field = ({ label, children, required, icon: Icon }) => (
  <label className="group block">
    <span className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-[#061A3A]">
      {Icon ? <Icon className="h-3.5 w-3.5 text-[#00A260]" /> : null}
      {label}
      {required ? <span className="text-[#F97316]">*</span> : null}
    </span>

    {children}
  </label>
);

const inputCls =
  "h-12 w-full rounded-xl border border-[#E2E8E5] bg-[#FAFCFB] px-3.5 text-sm font-medium text-[#061A3A] outline-none transition-all duration-200 placeholder:text-[#94A3B8] hover:border-[#CBD5D1] focus:border-[#00A260] focus:bg-white focus:ring-4 focus:ring-[#00A260]/10";

const textareaCls =
  "w-full rounded-xl border border-[#E2E8E5] bg-[#FAFCFB] p-3.5 text-sm font-medium text-[#061A3A] outline-none transition-all duration-200 placeholder:text-[#94A3B8] hover:border-[#CBD5D1] focus:border-[#00A260] focus:bg-white focus:ring-4 focus:ring-[#00A260]/10";

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#00A260]/10 text-[#00A260]">
      <Icon className="h-4 w-4" />
    </div>

    <div>
      <h4 className="text-sm font-extrabold text-[#061A3A]">{title}</h4>

      {description ? (
        <p className="mt-0.5 text-xs text-[#64748B]">{description}</p>
      ) : null}
    </div>
  </div>
);

const RideBookingForm = ({ onCancel }) => {
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    whatsapp: "",
    email: "",
    pickup_location: "",
    drop_location: "",
    travel_date: "",
    pickup_time: "",
    return_date: "",
    passengers: "",
    vehicle_type: "",
    service_type: "",
    requirements: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");

    if (!form.customer_name.trim() || !form.phone.trim()) {
      setError("Please enter your name and mobile number.");
      return;
    }

    if (!form.pickup_location.trim()) {
      setError("Please enter your pickup location.");
      return;
    }

    setSubmitting(true);

    try {
      await rideService.createRideRequest(form);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(
        err?.message ||
          "Unable to submit your ride request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <BookingSuccess onClose={onCancel} />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="animate-[fadeIn_.35s_ease-out] space-y-6"
    >
      {/* Intro */}
      <div className="rounded-2xl border border-[#00A260]/10 bg-gradient-to-br from-[#F3FBF7] to-white p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00A260] text-white shadow-sm">
            <Car className="h-5 w-5" />
          </div>

          <div>
            <h3 className="text-sm font-extrabold text-[#061A3A] sm:text-base">
              Plan Your Journey
            </h3>

            <p className="mt-1 text-xs leading-5 text-[#64748B] sm:text-sm">
              Share your travel details and our NJV team will contact you with
              the best vehicle and travel option.
            </p>
          </div>
        </div>
      </div>

      {/* Customer Details */}
      <section className="rounded-2xl border border-[#E2E8E5] bg-white p-4 shadow-sm sm:p-5">
        <SectionHeader
          icon={User}
          title="Your Details"
          description="Tell us how we can contact you."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full Name" required icon={User}>
            <input
              type="text"
              className={inputCls}
              placeholder="Enter your full name"
              value={form.customer_name}
              onChange={e => update("customer_name", e.target.value)}
              autoComplete="name"
            />
          </Field>

          <Field label="Mobile Number" required icon={Phone}>
            <input
              type="tel"
              className={inputCls}
              placeholder="10-digit mobile number"
              value={form.phone}
              onChange={e => update("phone", e.target.value)}
              autoComplete="tel"
            />
          </Field>

          <Field label="WhatsApp Number" icon={MessageCircle}>
            <input
              type="tel"
              className={inputCls}
              placeholder="WhatsApp number"
              value={form.whatsapp}
              onChange={e => update("whatsapp", e.target.value)}
              autoComplete="tel"
            />
          </Field>

          <Field label="Email Address">
            <input
              type="email"
              className={inputCls}
              placeholder="you@example.com"
              value={form.email}
              onChange={e => update("email", e.target.value)}
              autoComplete="email"
            />
          </Field>
        </div>
      </section>

      {/* Journey Details */}
      <section className="rounded-2xl border border-[#E2E8E5] bg-white p-4 shadow-sm sm:p-5">
        <SectionHeader
          icon={MapPin}
          title="Journey Details"
          description="Where and when are you travelling?"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Pickup Location" required icon={MapPin}>
            <input
              type="text"
              className={inputCls}
              placeholder="Where should we pick you up?"
              value={form.pickup_location}
              onChange={e => update("pickup_location", e.target.value)}
            />
          </Field>

          <Field label="Drop Location" icon={MapPin}>
            <input
              type="text"
              className={inputCls}
              placeholder="Where are you going?"
              value={form.drop_location}
              onChange={e => update("drop_location", e.target.value)}
            />
          </Field>

          <Field label="Travel Date" icon={CalendarDays}>
            <input
              type="date"
              className={inputCls}
              value={form.travel_date}
              onChange={e => update("travel_date", e.target.value)}
            />
          </Field>

          <Field label="Pickup Time" icon={Clock3}>
            <input
              type="time"
              className={inputCls}
              value={form.pickup_time}
              onChange={e => update("pickup_time", e.target.value)}
            />
          </Field>

          <Field label="Return Date">
            <input
              type="date"
              className={inputCls}
              value={form.return_date}
              onChange={e => update("return_date", e.target.value)}
            />
          </Field>

          <Field label="Passengers" icon={Users}>
            <input
              type="number"
              min="1"
              className={inputCls}
              placeholder="e.g. 4"
              value={form.passengers}
              onChange={e => update("passengers", e.target.value)}
            />
          </Field>
        </div>
      </section>

      {/* Vehicle & Service */}
      <section className="rounded-2xl border border-[#E2E8E5] bg-white p-4 shadow-sm sm:p-5">
        <SectionHeader
          icon={Car}
          title="Vehicle & Service"
          description="Choose the travel option that suits your needs."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vehicle">
            <select
              className={inputCls}
              value={form.vehicle_type}
              onChange={e => update("vehicle_type", e.target.value)}
            >
              <option value="">Select vehicle</option>
              <option>Coach</option>
              <option>Van</option>
              <option>Ertiga</option>
              <option>Toyota</option>
            </select>
          </Field>

          <Field label="Service Type">
            <select
              className={inputCls}
              value={form.service_type}
              onChange={e => update("service_type", e.target.value)}
            >
              <option value="">Select service</option>
              <option>Airport Transfer</option>
              <option>Outstation</option>
              <option>Local Travel</option>
              <option>One Way</option>
              <option>Round Trip</option>
              <option>Emergency Requirement</option>
            </select>
          </Field>
        </div>
      </section>

      {/* Requirements */}
      <section className="rounded-2xl border border-[#E2E8E5] bg-white p-4 shadow-sm sm:p-5">
        <SectionHeader
          icon={MessageCircle}
          title="Additional Requirements"
          description="Anything else we should know?"
        />

        <textarea
          rows="4"
          className={textareaCls}
          placeholder="Tell us about luggage, special requirements, stops, or anything else..."
          value={form.requirements}
          onChange={e => update("requirements", e.target.value)}
        />
      </section>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <X className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Bottom actions */}
      <div className="sticky bottom-0 -mx-4 border-t border-[#E2E8E5] bg-white/95 px-4 py-4 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:pt-1">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="h-12 rounded-xl border border-[#E2E8E5] px-6 text-sm font-bold text-[#061A3A] transition-all hover:border-[#CBD5D1] hover:bg-[#F8FAF9] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#00A260] px-7 text-sm font-bold text-white shadow-lg shadow-[#00A260]/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#005543] hover:shadow-xl hover:shadow-[#00A260]/25 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {submitting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Submitting Request...
              </>
            ) : (
              <>
                Request Ride
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </div>

        <p className="mt-3 text-center text-[11px] leading-4 text-[#94A3B8]">
          By submitting this request, our NJV team may contact you regarding
          your journey.
        </p>
      </div>
    </form>
  );
};

export default RideBookingForm;